import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, RefreshCw, Download, Send, Trash2, FileText, X, Eye, Pencil, Copy, Mail, DollarSign, Paperclip, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { ordenesCompraApi } from '../../utils/api';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';
import MonthRangePicker from '../../components/MonthRangePicker';
import ConfirmModal from '../../components/ConfirmModal';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50",
    success: "border border-green-400 text-green-600 hover:bg-green-50",
    amber: "border border-amber-400 text-amber-600 hover:bg-amber-50",
    danger: "border border-red-300 text-red-600 hover:bg-red-50",
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

const DATE_RANGES = {
  'hoy': { label: 'Hoy', getDates: () => { const t = new Date(); t.setHours(0,0,0,0); return { desde: t, hasta: new Date() }; }},
  'esta_semana': { label: 'Esta semana', getDates: () => { const t = new Date(); const f = new Date(t); f.setDate(t.getDate()-t.getDay()); f.setHours(0,0,0,0); return { desde: f, hasta: new Date() }; }},
  'este_mes': { label: 'Este mes', getDates: () => { const t = new Date(); return { desde: new Date(t.getFullYear(), t.getMonth(), 1), hasta: new Date() }; }},
  'mes_anterior': { label: 'Mes anterior', getDates: () => { const t = new Date(); const f = new Date(t.getFullYear(), t.getMonth()-1, 1); const l = new Date(t.getFullYear(), t.getMonth(), 0); l.setHours(23,59,59); return { desde: f, hasta: l }; }},
  'este_ano': { label: 'Este año', getDates: () => ({ desde: new Date(new Date().getFullYear(), 0, 1), hasta: new Date() }) },
  'ultimos_3_meses': { label: 'Últimos 3 meses', getDates: () => { const f = new Date(); f.setMonth(f.getMonth()-3); f.setHours(0,0,0,0); return { desde: f, hasta: new Date() }; }},
  'todo': { label: 'Todo el tiempo', getDates: () => ({ desde: null, hasta: null }) }
};

const formatLocalDate = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
};

export default function OrdenesCompra() {
  const navigate = useNavigate();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Filtro de período unificado (MonthRangePicker). Default = mes actual.
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('OrdenesCompra', () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return {
      modo: 'mes',
      periodo: `${y}-${m}`,
      desde: `${y}-${m}-01`,
      hasta: `${y}-${m}-${String(last).padStart(2, '0')}`
    };
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { searchRef, buttonsRef } = useToolbarResponsive([filtroFecha, selectedIds.size]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [emailOC, setEmailOC] = useState(null);
  const [pagoOC, setPagoOC] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOC, setPreviewOC] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: search, page, limit: 30 };
      if (filtroFecha?.desde) params.dateFrom = filtroFecha.desde;
      if (filtroFecha?.hasta) params.dateTo = filtroFecha.hasta;
      const res = await ordenesCompraApi.list(params);
      setOrdenes(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
      setTotalPages(res.data?.pagination?.pages || 1);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [search, page, filtroFecha]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // La selección solo guarda órdenes que se ven en la lista (al cambiar de mes quedaban marcadas sin verse)
  useEffect(() => {
    setSelectedIds(prev => {
      const visibles = new Set(ordenes.map(o => o.id));
      const quedan = [...prev].filter(id => visibles.has(id));
      return quedan.length === prev.size ? prev : new Set(quedan);
    });
  }, [ordenes]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedOCs = ordenes.filter(o => selectedIds.has(o.id));

  // Nombre de archivo: "OCXXXX - Proveedor.pdf" (sin caracteres inválidos)
  const buildOcFilename = (oc) => {
    const num = String(oc.numero || oc.id || '').trim();
    const withPrefix = /^oc/i.test(num) ? num : `OC${num}`;
    const proveedor = String(oc.proveedorNombre || '').trim();
    const base = proveedor ? `${withPrefix} - ${proveedor}` : withPrefix;
    return base.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim() + '.pdf';
  };

  const handleDownloadPdf = async (oc) => {
    const filename = buildOcFilename(oc);
    try {
      // 1) Pedir "Guardar como" primero (mientras el gesto del click está fresco).
      //    Chrome/Edge muestran el diálogo nativo con el nombre ya sugerido.
      let saveHandle = null;
      if (window.showSaveFilePicker) {
        try {
          saveHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }]
          });
        } catch (e) {
          if (e.name === 'AbortError') return; // el usuario canceló el diálogo
          saveHandle = null; // no soportado / bloqueado → fallback
        }
      }

      const res = await ordenesCompraApi.downloadPdf(oc.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });

      // 2a) Con diálogo nativo: escribir donde el usuario eligió.
      if (saveHandle) {
        const writable = await saveHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      }

      // 2b) Fallback: descarga directa con el nombre correcto.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error('Error descargando PDF');
    }
  };

  // Abre el modal para personalizar el correo antes de enviar.
  const handleSendEmail = (oc) => {
    setEmailOC(oc);
  };

  const handleDelete = async () => {
    try {
      for (const id of selectedIds) {
        await ordenesCompraApi.delete(id);
      }
      toast.success(`${selectedIds.size} orden(es) eliminada(s)`);
      setSelectedIds(new Set());
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error eliminando');
    }
  };

  const handlePreview = async (oc) => {
    setPreviewOC(oc);
    setPreviewLoading(true);
    try {
      const res = await ordenesCompraApi.downloadPdf(oc.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Error cargando PDF');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewOC(null);
  };

  const formatMoney = (val, moneda) => {
    const sym = moneda === 'USD' ? '$' : '₡';
    return `${sym}${parseFloat(val || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="p-4">
      {/* Título eliminado — ya lo muestra el header global del Layout */}

      {/* Toolbar — buscador absolute izq, botones centrados al toolbar (responsive con hook) */}
      <div className="bg-white rounded-lg border p-3 mb-4 relative">
        <div ref={searchRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar por número, proveedor..."
              className="pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-52 text-sm"
            />
          </div>
          <MonthRangePicker
            value={filtroFecha}
            onChange={(v) => { setFiltroFecha(v); setPage(1); }}
          />
        </div>
        <div ref={buttonsRef} className="flex items-center justify-center gap-2 flex-wrap">
            <ToolbarButton icon={Plus} label="Nueva OC" onClick={() => navigate('/compras/ordenes-compra/nueva')} variant="amber" />
            <ToolbarButton icon={RefreshCw} label="Actualizar" onClick={() => { fetchData(); toast.success('Actualizado'); }} />
            <ToolbarButton icon={FileText} label="Ver" onClick={() => selectedOCs[0] && handlePreview(selectedOCs[0])} disabled={selectedIds.size !== 1} variant="primary" />
            <ToolbarButton icon={Download} label="PDF" onClick={() => selectedOCs[0] && handleDownloadPdf(selectedOCs[0])} disabled={selectedIds.size !== 1} variant="primary" />
            <ToolbarButton icon={Send} label="Enviar Email" onClick={() => selectedOCs[0] && handleSendEmail(selectedOCs[0])} disabled={selectedIds.size !== 1 || sendingEmail} variant="success" />
            <ToolbarButton
              icon={selectedIds.size === 1 && selectedOCs[0]?.estadoPago === 'pagada' ? Eye : DollarSign}
              label={selectedIds.size === 1 && selectedOCs[0]?.estadoPago === 'pagada' ? 'Ver Pagos' : 'Pagar OC'}
              onClick={() => selectedOCs[0] && setPagoOC(selectedOCs[0])}
              disabled={selectedIds.size !== 1}
              variant="success"
            />
            <ToolbarButton icon={Copy} label="Copiar" onClick={async () => {
              const id = [...selectedIds][0];
              try {
                toast.loading('Duplicando...', { id: 'dup' });
                await ordenesCompraApi.duplicate(id);
                toast.success('OC duplicada', { id: 'dup' });
                fetchData();
                setSelectedIds(new Set());
              } catch (e) { toast.error(e.response?.data?.error || 'Error al duplicar', { id: 'dup' }); }
            }} disabled={selectedIds.size !== 1} />
            {/* No se borran OC con abonos: el ON DELETE CASCADE se llevaría los pagos. */}
            <ToolbarButton icon={Trash2} label="Eliminar" onClick={() => setShowDeleteModal(true)}
              disabled={selectedIds.size === 0 || selectedOCs.some(o => (o.totalPagado || 0) > 0)} variant="danger" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-auto max-h-[calc(var(--alto-contenido)-60px)]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b sticky top-0 z-10">
            <tr>
              <th className="px-3 py-3 w-10"><input type="checkbox" onChange={(e) => {
                setSelectedIds(e.target.checked ? new Set(ordenes.map(o => o.id)) : new Set());
              }} checked={selectedIds.size > 0 && selectedIds.size === ordenes.length} className="h-4 w-4 rounded" /></th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-600">Nº</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-600">Fecha</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-600">Proveedor</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-600">Ref. Cotización</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-600">Subtotal</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-600">Impuesto</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-600">Total</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-600">Moneda</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-600">Estado</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-400">Cargando...</td></tr>
            ) : ordenes.length === 0 ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-400">No hay órdenes de compra</td></tr>
            ) : ordenes.map(oc => (
              <tr key={oc.id} className={`hover:bg-gray-50 ${selectedIds.has(oc.id) ? 'bg-blue-50' : ''}`}>
                <td className="px-3 py-3">
                  <input type="checkbox" checked={selectedIds.has(oc.id)} onChange={() => toggleSelect(oc.id)} className="h-4 w-4 rounded" />
                </td>
                <td className="px-3 py-3 font-mono text-xs font-semibold text-amber-700">{oc.numero}</td>
                <td className="px-3 py-3 text-gray-600">{oc.fecha ? new Date(oc.fecha).toLocaleDateString('es-CR') : '-'}</td>
                <td className="px-3 py-3 font-medium">{oc.proveedorNombre}</td>
                <td className="px-3 py-3 text-gray-500 text-xs">{oc.cotizacionNumero || '-'}</td>
                <td className="px-3 py-3 text-right">{formatMoney(oc.subtotal, oc.codigoMoneda)}</td>
                <td className="px-3 py-3 text-right">{formatMoney(oc.totalImpuesto, oc.codigoMoneda)}</td>
                <td className="px-3 py-3 text-right font-bold">{formatMoney(oc.total, oc.codigoMoneda)}</td>
                <td className="px-3 py-3 text-center text-xs">{oc.codigoMoneda}</td>
                <td className="px-3 py-3 text-center">
                  {(() => {
                    const label = oc.estado === 'reenviada' ? 'Reenviada al Cliente' : oc.estado === 'enviada' ? 'Enviada' : 'Creada';
                    const color = oc.estado === 'reenviada' ? 'bg-indigo-100 text-indigo-700' : oc.estado === 'enviada' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700';
                    const estadoPago = oc.estadoPago || (oc.pagada ? 'pagada' : 'pendiente');
                    if (estadoPago === 'pagada') {
                      return (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700" title={oc.fechaPago ? `Pagada el ${new Date(oc.fechaPago).toLocaleDateString('es-CR')}` : 'Pagada'}>
                          <CheckCircle size={11} /> {label} y Pagada
                        </span>
                      );
                    }
                    if (estadoPago === 'parcial') {
                      return (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700"
                          title={`Pagado ${formatMoney(oc.totalPagado, oc.codigoMoneda)} · Restan ${formatMoney(oc.saldoPendiente, oc.codigoMoneda)}`}>
                          <DollarSign size={11} /> {label} · Pago Parcial {Math.round((oc.totalPagado / (oc.total || 1)) * 100)}%
                        </span>
                      );
                    }
                    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>{label}</span>;
                  })()}
                </td>
                <td className="px-3 py-3 text-center">
                  {/* Con abonos registrados no se edita: cambiar el total descuadraría el saldo. */}
                  {oc.estado === 'creada' && (
                    (oc.totalPagado || 0) > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-400 border border-gray-200 rounded cursor-not-allowed"
                        title="Tiene pagos registrados. Elimine los abonos desde &quot;Pagar OC&quot; para poder editar.">
                        <Pencil size={12} /> Editar
                      </span>
                    ) : (
                      <button onClick={() => navigate(`/compras/ordenes-compra/${oc.id}/editar`)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50" title="Editar">
                        <Pencil size={12} /> Editar
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {ordenes.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              {['CRC', 'USD'].map(moneda => {
                const items = ordenes.filter(o => o.codigoMoneda === moneda);
                if (items.length === 0) return null;
                const sym = moneda === 'USD' ? '$' : '₡';
                const totSub = items.reduce((s, o) => s + o.subtotal, 0);
                const totImp = items.reduce((s, o) => s + o.totalImpuesto, 0);
                const totTotal = items.reduce((s, o) => s + o.total, 0);
                return (
                  <tr key={moneda} className="font-semibold text-gray-800">
                    <td colSpan={5} className="px-3 py-2 text-right">Totales {moneda}:</td>
                    <td className="px-3 py-2 text-right">{sym}{totSub.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right">{sym}{totImp.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right">{sym}{totTotal.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td colSpan={3}></td>
                  </tr>
                );
              })}
            </tfoot>
          )}
        </table>
      </div>

      {/* Contador — esquina inferior izquierda, abajo de la tabla */}
      <div className="text-xs text-gray-500 mt-1 px-1">
        {total} {total === 1 ? 'orden de compra' : 'órdenes de compra'}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Anterior</button>
          <span className="text-sm text-gray-600">Página {page} de {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Siguiente</button>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-2">Eliminar Órdenes de Compra</h3>
            <p className="text-gray-600 mb-4">¿Eliminar {selectedIds.size} orden(es) seleccionada(s)? Solo se eliminan las que están en estado "Creada".</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewOC && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Orden de Compra {previewOC.numero} - {previewOC.proveedorNombre}
              </h2>
              <div className="flex items-center gap-2">
                <button onClick={() => handleDownloadPdf(previewOC)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-300 text-green-600 rounded-lg text-sm font-medium hover:bg-green-50">
                  <Download className="h-4 w-4" /> Guardar PDF
                </button>
                <button onClick={() => handleSendEmail(previewOC)} disabled={sendingEmail === previewOC.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 disabled:opacity-50">
                  <Send className="h-4 w-4" /> Enviar
                </button>
                <button onClick={closePreview} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-200 min-h-[70vh]">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
                  <span className="ml-3 text-gray-500">Cargando...</span>
                </div>
              ) : previewUrl ? (
                <iframe src={`${previewUrl}#navpanes=0&scrollbar=0`} className="w-full h-full min-h-[70vh]" title={`OC ${previewOC.numero}`} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">Error al cargar PDF</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enviar Email Modal */}
      {emailOC && (
        <EnviarOCModal
          oc={emailOC}
          totalFmt={formatMoney(emailOC.total, emailOC.codigoMoneda)}
          onClose={() => setEmailOC(null)}
          onSent={() => { setEmailOC(null); fetchData(); }}
        />
      )}

      {pagoOC && (
        <PagarOCModal
          oc={pagoOC}
          formatMoney={formatMoney}
          onClose={() => setPagoOC(null)}
          /* El modal queda abierto para seguir abonando: solo se refresca la lista de atrás. */
          onSaved={() => fetchData()}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PagarOCModal — abonos parciales de una OC (historial + saldo en vivo)
// ══════════════════════════════════════════════════════════════════════
const OC_MEDIOS = [
  { v: 'transferencia', l: 'Transferencia' },
  { v: 'sinpe', l: 'SINPE Móvil' },
  { v: 'cheque', l: 'Cheque' },
  { v: 'efectivo', l: 'Efectivo' },
  { v: 'tarjeta', l: 'Tarjeta' },
  { v: 'otro', l: 'Otro' },
];
const ocMedioLabel = (v) => OC_MEDIOS.find(m => m.v === v)?.l || v || '—';

// Un abono se guarda con 2 decimales; el saldo puede traer sub-céntimos del
// total de la OC, así que se considera saldada con un céntimo de tolerancia
// (mismo criterio que el backend).
const OC_TOLERANCIA = 0.01;
const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const PORCENTAJES = [25, 50, 75, 100];

function PagarOCModal({ oc, formatMoney, onClose, onSaved }) {
  const [cargando, setCargando] = useState(true);
  const [pagos, setPagos] = useState([]);
  const [resumen, setResumen] = useState({
    total: oc.total || 0,
    totalPagado: oc.totalPagado || 0,
    saldoPendiente: oc.saldoPendiente != null ? oc.saldoPendiente : (oc.total || 0),
    estadoPago: oc.estadoPago || 'pendiente'
  });

  // Formulario del nuevo abono
  const [fechaPago, setFechaPago] = useState(new Date().toISOString().slice(0, 10));
  const [monto, setMonto] = useState('');
  const [medioPago, setMedioPago] = useState('transferencia');
  const [notas, setNotas] = useState('');
  const [archivo, setArchivo] = useState(null);

  const [saving, setSaving] = useState(false);
  const [aBorrar, setABorrar] = useState(null);      // abono pendiente de confirmar
  const [borrando, setBorrando] = useState(false);

  // Preview inline del comprobante de un abono
  const [compPagoId, setCompPagoId] = useState(null);
  const [compUrl, setCompUrl] = useState(null);
  const [compType, setCompType] = useState(null);
  const [compLoading, setCompLoading] = useState(false);

  const busy = saving || borrando;
  const moneda = oc.codigoMoneda;

  const cargarPagos = useCallback(async () => {
    try {
      const res = await ordenesCompraApi.listarPagos(oc.id);
      setPagos(res.data?.data?.pagos || []);
      if (res.data?.data?.resumen) setResumen(res.data.data.resumen);
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo cargar el historial de pagos');
    } finally {
      setCargando(false);
    }
  }, [oc.id]);

  useEffect(() => { cargarPagos(); }, [cargarPagos]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape' && !busy && !aBorrar) onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [busy, aBorrar, onClose]);

  useEffect(() => () => { if (compUrl) window.URL.revokeObjectURL(compUrl); }, [compUrl]);

  const saldo = resumen.saldoPendiente;
  const saldada = saldo <= OC_TOLERANCIA;
  const montoNum = parseFloat(monto);
  const montoValido = Number.isFinite(montoNum) && montoNum > 0;
  const excede = montoValido && montoNum > saldo + OC_TOLERANCIA;
  // Restante que quedaría si se registra este abono — es el número que el
  // usuario quiere ver moverse mientras escribe.
  const restanteProyectado = Math.max(saldo - (montoValido && !excede ? montoNum : 0), 0);
  const pctPagado = resumen.total > 0 ? Math.min((resumen.totalPagado / resumen.total) * 100, 100) : 0;

  const aplicarPorcentaje = (pct) => {
    // El porcentaje es sobre el SALDO pendiente, no sobre el total: así el 100%
    // siempre salda la OC aunque ya haya abonos previos.
    setMonto(r2(saldo * (pct / 100)).toFixed(2));
  };

  const verComprobante = async (pago) => {
    if (compPagoId === pago.id) { setCompPagoId(null); return; }
    setCompLoading(true);
    try {
      const res = await ordenesCompraApi.descargarComprobantePago(oc.id, pago.id);
      // El backend puede devolver octet-stream → re-envolver con el MIME correcto
      // (según la extensión) para que el navegador lo renderice inline.
      const ext = (pago.comprobanteFilename || '').toLowerCase().split('.').pop();
      const mime = ext === 'pdf' ? 'application/pdf'
        : ext === 'png' ? 'image/png'
        : (ext === 'jpg' || ext === 'jpeg') ? 'image/jpeg'
        : ext === 'webp' ? 'image/webp'
        : ext === 'gif' ? 'image/gif'
        : (res.data.type && res.data.type !== 'application/octet-stream') ? res.data.type
        : 'application/octet-stream';
      if (compUrl) window.URL.revokeObjectURL(compUrl);
      setCompType(mime === 'application/pdf' ? 'pdf' : (mime.startsWith('image/') ? 'image' : 'other'));
      setCompUrl(window.URL.createObjectURL(new Blob([res.data], { type: mime })));
      setCompPagoId(pago.id);
    } catch {
      toast.error('No se pudo cargar el comprobante');
    } finally {
      setCompLoading(false);
    }
  };

  const guardar = async () => {
    if (!montoValido) { toast.error('Ingrese el monto del abono'); return; }
    if (excede) { toast.error('El abono excede el saldo pendiente'); return; }
    if (!fechaPago) { toast.error('Ingrese la fecha de pago'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('fechaPago', fechaPago);
      fd.append('monto', String(montoNum));
      fd.append('medioPago', medioPago);
      fd.append('notas', notas);
      if (archivo) fd.append('comprobante', archivo);
      const res = await ordenesCompraApi.registrarPago(oc.id, fd);
      toast.success(res.data?.message || 'Abono registrado');
      setMonto(''); setNotas(''); setArchivo(null);
      await cargarPagos();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al registrar el abono');
    } finally {
      setSaving(false);
    }
  };

  const borrarAbono = async () => {
    if (!aBorrar) return;
    setBorrando(true);
    try {
      await ordenesCompraApi.eliminarAbono(oc.id, aBorrar.id);
      toast.success('Abono eliminado');
      if (compPagoId === aBorrar.id) setCompPagoId(null);
      setABorrar(null);
      await cargarPagos();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al eliminar el abono');
    } finally {
      setBorrando(false);
    }
  };

  const Card = ({ label, valor, tono }) => (
    <div className={`flex-1 rounded-lg px-3 py-2 border ${tono}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-base font-bold tabular-nums">{valor}</div>
    </div>
  );

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-emerald-600 to-green-700 rounded-t-xl shrink-0">
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5" /> Pagos de {oc.numero}
            </h3>
            <p className="text-white/80 text-xs mt-0.5">{oc.proveedorNombre}</p>
          </div>
          <button onClick={() => !busy && onClose()} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-5">
          {/* ===== Resumen: Total / Pagado / Restante ===== */}
          <div>
            <div className="flex gap-2">
              <Card label="Total OC" valor={formatMoney(resumen.total, moneda)} tono="bg-gray-50 border-gray-200 text-gray-700" />
              <Card label="Pagado" valor={formatMoney(resumen.totalPagado, moneda)} tono="bg-green-50 border-green-200 text-green-700" />
              <Card
                label={montoValido && !excede && !saldada ? 'Restante (con este abono)' : 'Restante'}
                valor={formatMoney(restanteProyectado, moneda)}
                tono={restanteProyectado <= OC_TOLERANCIA ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}
              />
            </div>
            <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-green-500 transition-all duration-300" style={{ width: `${pctPagado}%` }} />
            </div>
            <div className="mt-1 text-[11px] text-gray-500 text-right">{pctPagado.toFixed(1)}% pagado</div>
          </div>

          {/* ===== Historial de abonos ===== */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Abonos registrados {pagos.length > 0 && <span className="text-gray-400 normal-case font-normal">({pagos.length})</span>}
            </div>
            {cargando ? (
              <div className="text-sm text-gray-400 py-3">Cargando…</div>
            ) : pagos.length === 0 ? (
              <div className="text-sm text-gray-400 py-3 border border-dashed border-gray-200 rounded-lg text-center">
                Sin abonos todavía
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Fecha</th>
                      <th className="px-3 py-2 text-right font-medium">Monto</th>
                      <th className="px-3 py-2 text-left font-medium">Medio</th>
                      <th className="px-3 py-2 text-left font-medium">Notas</th>
                      <th className="px-3 py-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pagos.map(p => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {p.fechaPago ? new Date(p.fechaPago).toLocaleDateString('es-CR') : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-green-700 tabular-nums whitespace-nowrap">
                          {formatMoney(p.monto, moneda)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{ocMedioLabel(p.medioPago)}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs max-w-[160px] truncate" title={p.notas || ''}>{p.notas || '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-1">
                            {p.comprobanteFilename && (
                              <button onClick={() => verComprobante(p)} disabled={compLoading}
                                title={compPagoId === p.id ? 'Ocultar comprobante' : 'Ver comprobante'}
                                className="p-1.5 rounded hover:bg-blue-50 text-blue-600 disabled:opacity-50">
                                <Paperclip className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button onClick={() => setABorrar(p)} disabled={busy}
                              title="Eliminar abono"
                              className="p-1.5 rounded hover:bg-red-50 text-red-500 disabled:opacity-50">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {compPagoId && compUrl && (
              <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                {compType === 'pdf' ? (
                  <iframe src={compUrl} title="comprobante" className="w-full" style={{ height: 420, border: 'none' }} />
                ) : compType === 'image' ? (
                  <img src={compUrl} alt="comprobante" className="w-full object-contain bg-white" style={{ maxHeight: 420 }} />
                ) : (
                  <div className="p-6 text-center text-sm text-gray-500">No se puede previsualizar este tipo de archivo.</div>
                )}
              </div>
            )}
          </div>

          {/* ===== Nuevo abono ===== */}
          {saldada ? (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-2 text-green-800 text-sm">
              <CheckCircle className="h-4 w-4 shrink-0" />
              Orden de compra pagada por completo. Para corregir, elimine un abono del historial.
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 p-4 space-y-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nuevo abono</div>

              {/* Atajos de % sobre el saldo pendiente */}
              <div>
                <div className="text-xs text-gray-500 mb-1.5">Aplicar % del saldo pendiente</div>
                <div className="grid grid-cols-4 gap-2">
                  {PORCENTAJES.map(pct => {
                    const valor = r2(saldo * (pct / 100));
                    const activo = montoValido && Math.abs(montoNum - valor) < 0.005;
                    return (
                      <button key={pct} type="button" onClick={() => aplicarPorcentaje(pct)} disabled={busy}
                        className={`rounded-lg border px-2 py-1.5 transition-colors disabled:opacity-50 ${
                          activo ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}>
                        <div className="text-sm font-semibold">{pct === 100 ? 'Saldar' : `${pct}%`}</div>
                        <div className="text-[10px] opacity-70 tabular-nums truncate">{formatMoney(valor, moneda)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Fecha de pago</label>
                  <input type="date" value={fechaPago} onChange={e => setFechaPago(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Monto del abono</label>
                  <input type="number" step="0.01" min="0" value={monto} onChange={e => setMonto(e.target.value)}
                    placeholder="0,00"
                    className={`w-full border rounded-lg px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 ${
                      excede ? 'border-red-400 focus:ring-red-200' : 'border-gray-200 focus:ring-green-200'
                    }`} />
                  {excede && (
                    <p className="mt-1 text-xs text-red-600">
                      Excede el saldo pendiente ({formatMoney(saldo, moneda)})
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Medio de pago</label>
                <select value={medioPago} onChange={e => setMedioPago(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200">
                  {OC_MEDIOS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Comprobante (opcional)</label>
                <input type="file" accept="image/*,application/pdf" onChange={e => setArchivo(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-gray-50 file:text-gray-700 file:text-xs" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Notas (opcional)</label>
                <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200 resize-none"
                  placeholder="Nº de referencia, banco, etc." />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl shrink-0">
          <button onClick={() => !busy && onClose()}
            className="px-5 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">
            Cerrar
          </button>
          {!saldada && (
            <button onClick={guardar} disabled={busy || !montoValido || excede}
              className="px-6 py-2 border-2 border-green-600 bg-green-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-green-700">
              <CheckCircle className="h-4 w-4" />
              {saving ? 'Guardando...' : (montoValido && !excede && restanteProyectado <= OC_TOLERANCIA ? 'Registrar y saldar' : 'Registrar abono')}
            </button>
          )}
        </div>
      </div>
    </div>

    <ConfirmModal
      isOpen={!!aBorrar}
      onClose={() => !borrando && setABorrar(null)}
      onConfirm={borrarAbono}
      title="Eliminar abono"
      message={aBorrar
        ? `Se eliminará el abono de ${formatMoney(aBorrar.monto, moneda)} del ${aBorrar.fechaPago ? new Date(aBorrar.fechaPago).toLocaleDateString('es-CR') : '—'} y el saldo se recalculará. Esta acción no se puede deshacer.`
        : ''}
      confirmText={borrando ? 'Eliminando...' : 'Eliminar'}
      variant="danger"
    />
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════
// EnviarOCModal — personalizar destinatario, asunto y mensaje antes de enviar
// ══════════════════════════════════════════════════════════════════════
function EnviarOCModal({ oc, totalFmt, onClose, onSent }) {
  const [to, setTo] = useState(oc.proveedorEmail || '');
  const [subject, setSubject] = useState(`Orden de Compra ${oc.numero} - ${oc.proveedorNombre || ''}`);
  const [mensaje, setMensaje] = useState(
    `Estimados ${oc.proveedorNombre || 'proveedor'},\n\nAdjunto encontrará nuestra Orden de Compra ${oc.numero} por un total de ${totalFmt}.\n\nFavor confirmar recepción.\n\nSaludos cordiales.`
  );
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [sending, onClose]);

  // Preview real del correo (mismo builder del backend), debounced al escribir.
  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await ordenesCompraApi.previewEmail(oc.id, { subject, message: mensaje });
        if (!cancelled) setPreviewHtml(res.data?.html || '');
      } catch {
        if (!cancelled) setPreviewHtml('<p style="font-family:sans-serif;color:#94a3b8;padding:24px;">No se pudo cargar la vista previa.</p>');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [oc.id, subject, mensaje]);

  const handleSend = async () => {
    if (!to.trim()) { toast.error('Ingrese un destinatario'); return; }
    setSending(true);
    try {
      await ordenesCompraApi.sendEmail(oc.id, { to: to.trim(), subject: subject.trim(), message: mensaje });
      toast.success(`OC enviada a ${to.trim()}`);
      onSent();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar email');
    } finally {
      setSending(false);
    }
  };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !sending && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-slate-700 to-slate-800 rounded-t-xl">
          <h3 className="font-semibold text-white flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5" /> Enviar Orden de Compra {oc.numero}
          </h3>
          <button onClick={() => !sending && onClose()} className="text-white/70 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Campos */}
          <div className="px-6 pt-5 space-y-3">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-500 w-16 text-right">Para:</label>
              <input
                type="text" value={to} onChange={(e) => setTo(e.target.value)}
                placeholder="email@proveedor.com (separá varios con , o ;)"
                className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-blue-400 transition-colors"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-500 w-16 text-right">Asunto:</label>
              <input
                type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-blue-400 transition-colors"
              />
            </div>
          </div>

          {/* Mensaje */}
          <div className="px-6 pt-4">
            <label className="block text-sm font-medium text-gray-500 mb-2">Mensaje:</label>
            <textarea
              value={mensaje} onChange={(e) => setMensaje(e.target.value)} rows={7}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all resize-none leading-relaxed"
            />
          </div>

          {/* Preview real del correo */}
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Así se verá el correo</p>
              {previewLoading && <div className="animate-spin h-3 w-3 border-2 border-blue-400 border-t-transparent rounded-full" />}
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-[#f1f5f9]">
              <iframe
                title="preview-correo"
                srcDoc={previewHtml}
                sandbox=""
                className="w-full"
                style={{ height: 520, border: 'none', display: 'block' }}
              />
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
              <FileText className="h-3.5 w-3.5" />
              <span>Se adjuntará automáticamente: <strong className="text-gray-600">{oc.numero}.pdf</strong></span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={() => !sending && onClose()}
            className="px-5 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors">
            Cancelar
          </button>
          <button onClick={handleSend} disabled={sending}
            className="px-6 py-2.5 border-2 border-blue-500 text-blue-600 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-blue-50 transition-colors">
            <Send className="h-4 w-4" /> {sending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}
